import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../components/AppIcon";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Modal from "../components/ui/Modal";
import CampaignForm, {
  INTERVIEW_TYPE_OPTIONS,
  campaignToForm,
  buildCampaignPayload,
  validateCampaignForm,
  localInputToIso,
} from "../components/CampaignForm";
import {
  getCampaign,
  updateCampaign,
  getBatchCredits,
  listStudents,
  listAssignments,
  assignCampaign,
  cancelAssignment,
  listInterviewTypes,
  listReferenceCompanies,
} from "../api/client";
import { buildAssignmentsCsv, campaignExportFilename } from "./campaign-export";

// CampaignDetail = one interview definition, its batch's credit pool, and the
// per-student assignments fanned out from it (assign + track). Editing the
// definition reuses the shared CampaignForm (batch locked). Assigning spends one
// credit per new student; cancelling an unstarted assignment releases it.

const PAGE_SIZE = 25;
const ASSIGN_MAX = 500; // mirrors the backend's per-request assign cap

const selectClasses =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

const searchClasses =
  "flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

const TYPE_LABEL = Object.fromEntries(INTERVIEW_TYPE_OPTIONS.map((t) => [t.value, t.label]));
const typeLabel = (v) => (v ? TYPE_LABEL[v] || String(v).replace(/_/g, " ") : "—");

const ASSIGNMENT_STATUSES = ["assigned", "started", "completed", "expired", "cancelled"];

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function AssignmentStatusPill({ status }) {
  const map = {
    assigned: "bg-primary/10 text-primary",
    started: "bg-warning/15 text-warning",
    completed: "bg-success/15 text-success",
    expired: "bg-muted text-muted-foreground",
    cancelled: "bg-muted text-muted-foreground",
  };
  return (
    <span
      className={
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize " +
        (map[status] || "bg-muted text-muted-foreground")
      }
    >
      {status || "assigned"}
    </span>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-foreground">{value}</dd>
    </div>
  );
}

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [loadingCampaign, setLoadingCampaign] = useState(true);
  const [campaignError, setCampaignError] = useState("");
  const [credits, setCredits] = useState(null);

  // Assignments table
  const [assignments, setAssignments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [assignmentsError, setAssignmentsError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");

  // Reference catalogs for the edit form (fetched from the DB so a campaign's
  // type/company always map to a real engine flow — same source as the create form).
  const [interviewTypes, setInterviewTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [referenceLoading, setReferenceLoading] = useState(true);

  // Assign modal
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignStudents, setAssignStudents] = useState([]);
  const [assignedMap, setAssignedMap] = useState({});
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignStudentsError, setAssignStudentsError] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [selected, setSelected] = useState({});
  const [dueAt, setDueAt] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [assignResult, setAssignResult] = useState(null);

  // Cancel confirm
  const [toCancel, setToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState("");

  const loadCampaign = useCallback(async () => {
    setLoadingCampaign(true);
    setCampaignError("");
    try {
      const data = await getCampaign(id);
      setCampaign(data);
    } catch (err) {
      setCampaignError(err?.error || "Could not load the campaign.");
    } finally {
      setLoadingCampaign(false);
    }
  }, [id]);

  const loadCredits = useCallback(async (batchId) => {
    if (!batchId) return;
    try {
      const data = await getBatchCredits(batchId);
      setCredits(data);
    } catch {
      /* the credits line just shows a dash */
    }
  }, []);

  const loadAssignments = useCallback(async () => {
    setLoadingAssignments(true);
    setAssignmentsError("");
    try {
      const params = { campaignId: id, limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const data = await listAssignments(params);
      setAssignments(data.assignments || []);
      setTotal(data.total || 0);
    } catch (err) {
      setAssignmentsError(err?.error || "Could not load assignments.");
    } finally {
      setLoadingAssignments(false);
    }
  }, [id, page, statusFilter, debouncedSearch]);

  // Load the interview-type + company catalogs once (edit form falls back to a
  // built-in type list if this fails, so a failure never blocks editing).
  const loadReference = useCallback(async () => {
    setReferenceLoading(true);
    try {
      const [types, cos] = await Promise.all([
        listInterviewTypes().catch(() => []),
        listReferenceCompanies().catch(() => []),
      ]);
      setInterviewTypes(Array.isArray(types) ? types : []);
      setCompanies(Array.isArray(cos) ? cos : []);
    } finally {
      setReferenceLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  useEffect(() => {
    if (campaign?.placementSessionId) loadCredits(campaign.placementSessionId);
  }, [campaign?.placementSessionId, loadCredits]);

  useEffect(() => {
    loadReference();
  }, [loadReference]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const refreshAfterChange = useCallback(async () => {
    await Promise.all([
      loadCampaign(),
      loadAssignments(),
      campaign?.placementSessionId ? loadCredits(campaign.placementSessionId) : Promise.resolve(),
    ]);
  }, [loadCampaign, loadAssignments, loadCredits, campaign?.placementSessionId]);

  const exportAssignments = async () => {
    setExporting(true);
    setAssignmentsError("");
    try {
      const exportedAssignments = [];
      const baseParams = { campaignId: id, limit: ASSIGN_MAX, offset: 0 };
      if (statusFilter) baseParams.status = statusFilter;
      if (debouncedSearch.trim()) baseParams.search = debouncedSearch.trim();

      do {
        const data = await listAssignments({ ...baseParams, offset: exportedAssignments.length });
        exportedAssignments.push(...(data.assignments || []));
        if (!data.assignments?.length || exportedAssignments.length >= (data.total || 0)) break;
      } while (true);

      const csv = buildAssignmentsCsv(campaign.name, exportedAssignments);
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = campaignExportFilename(campaign.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setAssignmentsError(err?.error || "Could not export assignments.");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const creditsRemaining = credits?.creditsRemaining ?? 0;
  const canAssign =
    campaign && campaign.status !== "closed" && campaign.status !== "archived";

  const topics = Array.isArray(campaign?.topics) ? campaign.topics : [];

  // ── Edit ─────────────────────────────────────────────────────────────────
  const setEditField = (key) => (e) =>
    setEditForm((f) => ({ ...f, [key]: e.target.value }));

  const openEdit = () => {
    setEditForm(campaignToForm(campaign));
    setEditError("");
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e?.preventDefault?.();
    const err = validateCampaignForm(editForm, { requireBatch: false });
    if (err) {
      setEditError(err);
      return;
    }
    setSavingEdit(true);
    setEditError("");
    try {
      await updateCampaign(id, buildCampaignPayload(editForm, { isEdit: true }));
      setEditOpen(false);
      await refreshAfterChange();
    } catch (e2) {
      setEditError(e2?.error || "Could not save the campaign.");
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Assign ───────────────────────────────────────────────────────────────
  const openAssign = async () => {
    if (!campaign) return;
    setAssignOpen(true);
    setSelected({});
    setStudentSearch("");
    setDueAt("");
    setAssignError("");
    setAssignResult(null);
    setAssignStudentsError("");
    setAssignLoading(true);
    try {
      const [studentsData, assignmentsData] = await Promise.all([
        listStudents({ placementSessionId: campaign.placementSessionId, limit: ASSIGN_MAX }),
        listAssignments({ campaignId: campaign.id, limit: ASSIGN_MAX }),
      ]);
      setAssignStudents(studentsData.students || []);
      const map = {};
      (assignmentsData.assignments || []).forEach((a) => {
        map[a.studentId] = a.status;
      });
      setAssignedMap(map);
    } catch (err) {
      setAssignStudentsError(err?.error || "Could not load the batch's students.");
    } finally {
      setAssignLoading(false);
    }
  };

  const studentNameById = useMemo(
    () => Object.fromEntries(assignStudents.map((s) => [s.id, s.name])),
    [assignStudents]
  );

  const filteredStudents = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    if (!q) return assignStudents;
    return assignStudents.filter((s) =>
      `${s.name || ""} ${s.email || ""} ${s.rollNumber || ""}`.toLowerCase().includes(q)
    );
  }, [assignStudents, studentSearch]);

  const eligibleFiltered = useMemo(
    () => filteredStudents.filter((s) => !assignedMap[s.id]),
    [filteredStudents, assignedMap]
  );

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((k) => selected[k]),
    [selected]
  );

  const allEligibleSelected =
    eligibleFiltered.length > 0 && eligibleFiltered.every((s) => selected[s.id]);

  const overCapacity = selectedIds.length > creditsRemaining;

  const toggleStudent = (sid) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[sid]) delete next[sid];
      else next[sid] = true;
      return next;
    });

  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = { ...prev };
      if (allEligibleSelected) eligibleFiltered.forEach((s) => delete next[s.id]);
      else eligibleFiltered.forEach((s) => (next[s.id] = true));
      return next;
    });

  const submitAssign = async () => {
    if (selectedIds.length === 0) {
      setAssignError("Select at least one student to assign.");
      return;
    }
    if (overCapacity) {
      setAssignError(
        `You've selected ${selectedIds.length} students but only ${creditsRemaining} credits remain.`
      );
      return;
    }
    setAssigning(true);
    setAssignError("");
    try {
      const payload = { studentIds: selectedIds };
      if (dueAt) {
        const iso = localInputToIso(dueAt);
        if (iso) payload.dueAt = iso;
      }
      const result = await assignCampaign(campaign.id, payload);
      setAssignResult(result);
      setSelected({});
      await refreshAfterChange();
    } catch (err) {
      setAssignError(err?.error || "Could not assign the campaign.");
    } finally {
      setAssigning(false);
    }
  };

  // ── Cancel ───────────────────────────────────────────────────────────────
  const confirmCancel = async () => {
    if (!toCancel) return;
    setCancelling(true);
    setCancelError("");
    try {
      await cancelAssignment(toCancel.id);
      setToCancel(null);
      await refreshAfterChange();
    } catch (err) {
      setCancelError(err?.error || "Could not cancel this assignment.");
    } finally {
      setCancelling(false);
    }
  };

  // ── Render: loading / error gates ──────────────────────────────────────────
  if (loadingCampaign) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="h-6 w-32 animate-pulse rounded bg-muted/50" />
        <div className="h-40 animate-pulse rounded-xl border border-border bg-muted/40" />
        <div className="h-64 animate-pulse rounded-xl border border-border bg-muted/40" />
      </div>
    );
  }

  if (campaignError || !campaign) {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <button
          onClick={() => navigate("/institution/campaigns")}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <Icon name="ArrowLeft" size={16} /> Campaigns
        </button>
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          <span>{campaignError || "Campaign not found."}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate("/institution/campaigns")}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <Icon name="ArrowLeft" size={16} /> Campaigns
      </button>

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="truncate text-2xl font-semibold text-foreground">{campaign.name}</h1>
            <AssignmentStatusPill status={campaign.status} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {[campaign.role, campaign.company].filter(Boolean).join(" · ") || campaign.batchName}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button variant="outline" iconName="Download" onClick={exportAssignments} loading={exporting}>
            Export
          </Button>
          <Button variant="outline" iconName="Pencil" onClick={openEdit}>
            Edit
          </Button>
          <Button
            iconName="Send"
            onClick={openAssign}
            disabled={!canAssign}
            title={canAssign ? undefined : `This campaign is ${campaign.status} and can't take new assignments.`}
          >
            Assign students
          </Button>
        </div>
      </div>

      {/* Config summary */}
      <div className="rounded-xl border border-border bg-card p-5 sm:p-6">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
          <Field
            label="Batch"
            value={
              <button
                onClick={() =>
                  navigate(`/institution/students?batch=${encodeURIComponent(campaign.placementSessionId)}`)
                }
                className="text-primary hover:underline"
              >
                {campaign.batchName}
              </button>
            }
          />
          <Field label="Role" value={campaign.role || "—"} />
          <Field label="Company" value={campaign.company || "—"} />
          <Field label="Interview type" value={typeLabel(campaign.interviewType)} />
          <Field label="Difficulty" value={campaign.difficulty || "Medium"} />
          <Field label="Duration" value={`${campaign.duration ?? 30} min`} />
          <Field label="Deadline" value={fmtDateTime(campaign.deadlineAt)} />
          <Field
            label="Credits left in batch"
            value={
              credits == null
                ? "…"
                : `${credits.creditsRemaining ?? 0} / ${credits.includedInterviewCredits ?? 0}`
            }
          />
        </dl>

        {topics.length > 0 && (
          <div className="mt-5">
            <div className="text-xs text-muted-foreground">Topics</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {topics.map((t, i) => (
                <span
                  key={i}
                  className="inline-flex items-center rounded-md bg-muted/60 px-2 py-1 text-xs text-foreground"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {campaign.instructions && (
          <div className="mt-5">
            <div className="text-xs text-muted-foreground">Instructions</div>
            <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
              {campaign.instructions}
            </p>
          </div>
        )}
      </div>

      {/* Assignments */}
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-base font-semibold text-foreground">
            Assignments
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              {campaign.assignedCount ?? 0} total · {campaign.completedCount ?? 0} completed
            </span>
          </h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                <Icon name="Search" size={16} />
              </span>
              <input
                type="text"
                placeholder="Search student"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={searchClasses + " sm:w-56"}
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              className={selectClasses + " sm:w-40"}
            >
              <option value="">All statuses</option>
              {ASSIGNMENT_STATUSES.map((s) => (
                <option key={s} value={s} className="capitalize">
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {assignmentsError && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <Icon name="AlertCircle" size={16} />
            <span>{assignmentsError}</span>
            <button onClick={loadAssignments} className="ml-auto font-medium underline">
              Retry
            </button>
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Student</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Assigned</th>
                  <th className="px-4 py-3 font-medium">Due</th>
                  <th className="px-4 py-3 font-medium">Score</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {loadingAssignments ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="px-4 py-3" colSpan={6}>
                        <div className="h-5 w-full animate-pulse rounded bg-muted/50" />
                      </td>
                    </tr>
                  ))
                ) : assignments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-14 text-center">
                      <div className="flex flex-col items-center">
                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Icon name="Users" size={22} />
                        </span>
                        <p className="mt-3 text-sm font-medium text-foreground">
                          {statusFilter || debouncedSearch
                            ? "No matching assignments"
                            : "No students assigned yet"}
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {statusFilter || debouncedSearch
                            ? "Try a different search or status filter."
                            : "Assign this campaign to students to get started."}
                        </p>
                        {!statusFilter && !debouncedSearch && canAssign && (
                          <Button className="mt-4" size="sm" iconName="Send" onClick={openAssign}>
                            Assign students
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  assignments.map((a) => (
                    <tr
                      key={a.id}
                      className="border-b border-border last:border-0 hover:bg-muted/30"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{a.studentName}</div>
                        <div className="text-xs text-muted-foreground">{a.studentEmail}</div>
                      </td>
                      <td className="px-4 py-3">
                        <AssignmentStatusPill status={a.status} />
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{fmtDate(a.assignedAt)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{fmtDate(a.dueAt)}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {a.score != null ? a.score : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {a.status === "assigned" ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Ban"
                            onClick={() => {
                              setToCancel(a);
                              setCancelError("");
                            }}
                          >
                            Cancel
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronLeft"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Prev
              </Button>
              <span className="px-1">
                Page {page + 1} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronRight"
                iconPosition="right"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ── Edit modal ── */}
      <Modal
        open={editOpen}
        onClose={() => (savingEdit ? null : setEditOpen(false))}
        title="Edit campaign"
        description="Update this interview definition."
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditOpen(false)} disabled={savingEdit}>
              Cancel
            </Button>
            <Button onClick={submitEdit} loading={savingEdit} iconName="Check">
              Save changes
            </Button>
          </>
        }
      >
        {editForm && (
          <form onSubmit={submitEdit}>
            <CampaignForm
              form={editForm}
              onField={setEditField}
              batches={[{ id: campaign.placementSessionId, name: campaign.batchName }]}
              lockBatch
              error={editError}
              interviewTypes={interviewTypes}
              companies={companies}
              referenceLoading={referenceLoading}
            />
            <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
          </form>
        )}
      </Modal>

      {/* ── Assign modal ── */}
      <Modal
        open={assignOpen}
        onClose={() => (assigning ? null : setAssignOpen(false))}
        title="Assign students"
        description={`Reserves one credit per student from ${campaign.batchName}.`}
        size="lg"
        footer={
          assignResult ? (
            <Button onClick={() => setAssignOpen(false)} iconName="Check">
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setAssignOpen(false)} disabled={assigning}>
                Cancel
              </Button>
              <Button
                onClick={submitAssign}
                loading={assigning}
                iconName="Send"
                disabled={selectedIds.length === 0 || overCapacity || assignLoading}
              >
                Assign{selectedIds.length > 0 ? ` ${selectedIds.length}` : ""}{" "}
                {selectedIds.length === 1 ? "student" : "students"}
              </Button>
            </>
          )
        }
      >
        {assignResult ? (
          <div className="space-y-4">
            <div
              className={
                "flex items-start gap-3 rounded-lg border px-4 py-3 " +
                (assignResult.assigned > 0
                  ? "border-success/30 bg-success/10"
                  : "border-warning/30 bg-warning/10")
              }
            >
              <Icon
                name={assignResult.assigned > 0 ? "CheckCircle2" : "Info"}
                size={20}
                className={
                  "mt-0.5 flex-shrink-0 " +
                  (assignResult.assigned > 0 ? "text-success" : "text-warning")
                }
              />
              <div className="text-sm">
                <div className="font-medium text-foreground">
                  {assignResult.assigned > 0
                    ? `Assigned to ${assignResult.assigned} student${
                        assignResult.assigned === 1 ? "" : "s"
                      }`
                    : "No new assignments were made"}
                </div>
                <div className="text-muted-foreground">
                  {assignResult.skippedDuplicate > 0 &&
                    `${assignResult.skippedDuplicate} already assigned. `}
                  {assignResult.skipped?.length > 0 && `${assignResult.skipped.length} skipped. `}
                  {`${assignResult.creditsRemaining} credit${
                    assignResult.creditsRemaining === 1 ? "" : "s"
                  } remaining.`}
                </div>
              </div>
            </div>

            {assignResult.skipped?.length > 0 && (
              <div className="rounded-lg border border-border">
                <div className="border-b border-border bg-muted/40 px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Skipped
                </div>
                <ul className="max-h-52 divide-y divide-border overflow-y-auto text-sm">
                  {assignResult.skipped.map((r, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 px-4 py-2">
                      <span className="truncate text-foreground">
                        {studentNameById[r.studentId] || r.studentId}
                      </span>
                      <span className="flex-shrink-0 text-muted-foreground">{r.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-4 py-2.5 text-sm">
              <span className="text-muted-foreground">
                Assigning in <span className="font-medium text-foreground">{campaign.batchName}</span>
              </span>
              <span
                className={"font-medium " + (creditsRemaining > 0 ? "text-foreground" : "text-destructive")}
              >
                {creditsRemaining} credit{creditsRemaining === 1 ? "" : "s"} left
              </span>
            </div>

            {assignLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-11 animate-pulse rounded bg-muted/50" />
                ))}
              </div>
            ) : assignStudentsError ? (
              <p className="flex items-center gap-1.5 text-sm text-destructive">
                <Icon name="AlertCircle" size={14} /> {assignStudentsError}
              </p>
            ) : assignStudents.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                This batch has no students yet.{" "}
                <button
                  onClick={() =>
                    navigate(`/institution/students?batch=${encodeURIComponent(campaign.placementSessionId)}`)
                  }
                  className="font-medium text-primary underline"
                >
                  Add students
                </button>{" "}
                first.
              </div>
            ) : (
              <>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    <Icon name="Search" size={16} />
                  </span>
                  <input
                    type="text"
                    placeholder="Search by name, email or roll number"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    className={searchClasses}
                  />
                </div>

                <div className="flex items-center justify-between px-1 text-sm">
                  <label className="inline-flex cursor-pointer items-center gap-2 text-muted-foreground">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-input accent-primary"
                      checked={allEligibleSelected}
                      onChange={toggleSelectAll}
                      disabled={eligibleFiltered.length === 0}
                    />
                    Select all{eligibleFiltered.length > 0 ? ` (${eligibleFiltered.length})` : ""}
                  </label>
                  <span className={overCapacity ? "text-destructive" : "text-muted-foreground"}>
                    {selectedIds.length} selected
                  </span>
                </div>

                {overCapacity && (
                  <p className="flex items-start gap-1.5 text-sm text-destructive">
                    <Icon name="AlertCircle" size={14} className="mt-0.5 flex-shrink-0" />
                    You&apos;ve selected more students than the {creditsRemaining} credits remaining.
                    Reduce the selection or add credits to the batch.
                  </p>
                )}

                <div className="max-h-72 divide-y divide-border overflow-y-auto rounded-lg border border-border">
                  {filteredStudents.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                      No students match your search.
                    </div>
                  ) : (
                    filteredStudents.map((s) => {
                      const assignedStatus = assignedMap[s.id];
                      return (
                        <label
                          key={s.id}
                          className={
                            "flex items-center gap-3 px-4 py-2.5 text-sm " +
                            (assignedStatus
                              ? "cursor-not-allowed opacity-60"
                              : "cursor-pointer hover:bg-muted/30")
                          }
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-input accent-primary"
                            checked={!!selected[s.id]}
                            disabled={!!assignedStatus}
                            onChange={() => toggleStudent(s.id)}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium text-foreground">
                              {s.name}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {s.email}
                              {s.rollNumber ? ` · ${s.rollNumber}` : ""}
                            </span>
                          </span>
                          {assignedStatus && (
                            <span className="flex-shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs capitalize text-muted-foreground">
                              {assignedStatus}
                            </span>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>

                <Input
                  label="Due date (optional)"
                  type="datetime-local"
                  description="Defaults to the campaign deadline if left blank."
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                />

                {assignError && (
                  <p className="flex items-start gap-1.5 text-sm text-destructive">
                    <Icon name="AlertCircle" size={14} className="mt-0.5 flex-shrink-0" />
                    {assignError}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      {/* ── Cancel confirm ── */}
      <Modal
        open={!!toCancel}
        onClose={() => (cancelling ? null : setToCancel(null))}
        title="Cancel assignment"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setToCancel(null)} disabled={cancelling}>
              Keep it
            </Button>
            <Button variant="danger" onClick={confirmCancel} loading={cancelling} iconName="Ban">
              Cancel assignment
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          Cancel the assignment for{" "}
          <span className="font-medium text-foreground">{toCancel?.studentName}</span>? Their
          reserved credit is released back to the batch pool.
        </p>
        {cancelError && (
          <p className="mt-3 flex items-start gap-1.5 text-sm text-destructive">
            <Icon name="AlertCircle" size={14} className="mt-0.5 flex-shrink-0" /> {cancelError}
          </p>
        )}
      </Modal>
    </div>
  );
}
